import React, { useState, useEffect, useRef } from 'react';
import { TitleBar } from 'styles/layout/titlebar';
import JsxHelper from 'helpers/jsx';
import GitHelper from 'helpers/git';
import DialogHelper from 'helpers/dialog';
import WebsiteHelper from 'helpers/website';
import useTitle from 'hooks/useTitle';
import useModal from 'hooks/useModal';
import useConfirm from 'hooks/useConfirm';
import WebsiteService from 'services/website';
import WebsiteOptionsTrait from 'components/website/CreateWebsiteOptions/WebsiteOptionsTrait';
import { connectWebsiteToGit, disconnectWebsiteFromGit } from 'store/website/websiteActions';
import { Content } from 'styles/globalStyles';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { WPSForm } from 'styles/layout/forms';
import { useHistory } from 'react-router-dom';
import { Container } from 'styles/website/profile';
import env from 'config/env';
import EventLogsTable from "components/package/Git/EventLogsTable";
import 'components/stepper/stepper.css';

const ConnectGit = ({ website }) => {
    useTitle(`Website - Git Integration`);
    const dispatch = useDispatch();
    const history = useHistory();
    const RESOURCE_TYPE = 'WordPress';
    const WPSFORM_STYLE = { maxWidth: '1000px', margin: '40px 0 0 15px' };
    const GIT_OPTIONS = [
      { label: 'Disable Plugins / Themes Auto-Updater', name: 'autoupdater_off', default: true, tooltip: 'Check this box to disable the Plugins / Themes Auto-Updater for this website. This helps prevent conflicts with your Git repository.' },
      { label: 'Disable Media Uploads Sync', name: 'uploads_sync_off', default: true, tooltip: 'Check this box to disable syncing files from the wp-content/uploads directory in your Git repository to your website. This helps prevent excessive storage usage, which may lead to additional charges.' },
      { label: 'Purge Cache After Sync', name: 'purge_cache_sync', default: true, tooltip: 'Check this box to purge the cache after syncing files from your Git repository to your website. This helps ensure that the latest changes are reflected on your website.' },
    ];
    const STEPS_NUM = 4;
    const mounted = useRef(true);
    const modalDialog = useModal();
    const confirm = useConfirm();
    const isGitConnected = () => website.is_git;
    const subheader = <p>
      {env.getBrandShortName()} makes it easy to connect your website to a Git repository branch, giving you the ability to deploy changes to your website with ease.
      <div className='margin-top-5'>
        For more information, please refer to our {JsxHelper.createFaqLink('integrating-git-with-staq', 'Git Integration Guide')}.
      </div>
    </p>
    const { handleSubmit } = useForm({ reValidateMode: 'onSubmit' });
    const [sshLoading, setSshLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [gitInfo, setGitInfo] = useState(false);
    const [gitEventLog, setGitEventLog] = useState(false);
    const [repositoryError, setRepositoryError] = useState(false);
    const [repositoryUrl, setRepositoryUrl] = useState('');
    const [repositoryBranch, setRepositoryBranch] = useState('');
    const [sshPublicKey, setSshPublicKey] = useState(false);
    const [sshKeysId, setSshKeysId] = useState(false);
    const [webhookApiUrl, setWebhookApiUrl] = useState(false);
    const [webhookApiKey, setWebhookApiKey] = useState(false);
    const [confirmWebhookAdd, setConfirmWebhookAdd] = useState(false);
    const [gitService, setGitService] = useState('bitbucket');
    const [optionsChanged, setOptionsChanged] = useState(false);
    const [gitOptions, setGitOptions] = useState(GIT_OPTIONS.reduce((acc, option) => {
      acc[option.name] = option.default;
      return acc;
    }, {}));
  
    // ------------------------------
    // LIFECYCLE METHODS
    // ------------------------------
  
    useEffect(() => {
      if (!isGitConnected()) {
        initStepper(1);
      } else {
        getGitSettings();
      }
      return () => {
        mounted.current = false;
      };
      // eslint-disable-next-line
    }, []);

    // ------------------------------
    // HANDLER METHODS
    // ------------------------------

    const handleOnChange = e => {
      const { name, type } = e.target;
      const value = type === 'checkbox' ? e.target.checked : e.target.value;
      setGitOptions({...gitOptions, [name]: value});
    };

    // -------------------------------
    // API CALLS
    // -------------------------------

    const connectGit = () => {
      setLoading(true);
      const data = {
        website_slug: website.slug,
        repository_url: repositoryUrl,
        repository_branch: repositoryBranch,
        ssh_keys_id: sshKeysId,
        webhook_api_url: webhookApiUrl,
        webhook_api_key: webhookApiKey,
        autoupdater_off: gitOptions.autoupdater_off,
        uploads_sync_off: gitOptions.uploads_sync_off,
        purge_cache_sync: gitOptions.purge_cache_sync,
      }
      dispatch(connectWebsiteToGit(data))
        .then(() => {
          WebsiteHelper.goToStaqPanel(history, website.slug);
          DialogHelper.success(modalDialog, 'Your website is now connected to your Git repository.'); 
        })
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }

    const disconnectGit = () => {
      setLoading('disconnect');
      dispatch(disconnectWebsiteFromGit({ website_slug: website.slug }))
        .then(() => {
          WebsiteHelper.goToStaqPanel(history, website.slug);
          DialogHelper.success(modalDialog, 'Git repository disconnected successfully.');
        })
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }

    const getGitSettings = () => {
      setLoading('git-info');
      WebsiteService.getGitSettings({ website_slug: website.slug })
        .then(res => {
          let options = {};
          GIT_OPTIONS.forEach(option => {
            options[option.name] = res[option.name] !== undefined ? res[option.name] : option.default;
            res[option.name] = options[option.name]; // Set default value if not present
          });
          setGitInfo(res);
          setGitOptions(options);
        })
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }

    const forceGitPull = () => {
      setLoading('force-pull');
      WebsiteService.pullGit({ website_slug: website.slug })
        .then(() => DialogHelper.success(modalDialog, 'Git pull was successful.'))
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }

    const listGitEventLogs = () => {
      setLoading('event-log');
      WebsiteService.fetchGitEvents({ website_slug: website.slug })
        .then(logs => {
          if (logs.length === 0) {
            DialogHelper.info(modalDialog, 'No event logs found for this website.');
          } else {
            setGitEventLog(logs);
          }
        })
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }

    const onGenerateKeys = () => {
      setSshLoading(true);
      WebsiteService.generateGitKeys({ website_slug: website.slug })
        .then(res => {
          setSshPublicKey(res.ssh_public_key);
          setSshKeysId(res.ssh_keys_id);
          setWebhookApiUrl(res.webhook_api_url);
          setWebhookApiKey(res.webhook_api_key);
          JsxHelper.copyToClipboard(dispatch, res.ssh_public_key, 'SSH key');
        })
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setSshLoading(false));
    }
  
    const onConnectRepository = async () => {
      setLoading(true);
      const data = {
        website_slug: website.slug,
        repository_url: repositoryUrl,
        repository_branch: repositoryBranch,
        ssh_keys_id: sshKeysId,
      }
      WebsiteService.isGitClonable(data)
        .then(() => goToStep(3))
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }

    const saveGitOptions = () => {
      setLoading('save-options');
      const data = {
        website_slug: website.slug,
        autoupdater_off: gitOptions.autoupdater_off,
        uploads_sync_off: gitOptions.uploads_sync_off,
        purge_cache_sync: gitOptions.purge_cache_sync,
      };
      WebsiteService.saveGitOptions(data)
        .then((res) => {
          setGitInfo({ ...gitInfo, ...res });
          setGitOptions(res);
          setOptionsChanged(false);
          DialogHelper.success(modalDialog, 'Git options saved successfully.');
        })
        .catch(error => DialogHelper.error(modalDialog, error))
        .finally(() => setLoading(false));
    }
  
    // ------------------------------
    // STEPPER METHODS
    // ------------------------------
  
    const [currentStep, setCurrentStep] = useState(1);
    const [currentStepForData, setCurrentStepForData] = useState(false);
    const [disabledStepsData, setDisabledStepsData] = useState([]);
    const stepperRef = useRef();
    const disabledStepsElements = useRef([]);
  
    const initStepper = (step) => {
      // Set disabled steps
      let disabledSteps = [];
      for (let i = step; i <= stepperSteps.length; i++) {
        disabledSteps.push(i);
      }
      setDisabledStepsData(disabledSteps);
      disabledStepsElements.current = disabledSteps;
  
      // Initialize steps and go to initial
      stepperSteps[step - 1].onClick();
  
      // Set initial step at the end of conditions
      setCurrentStep(step + 1);
      setCurrentStepForData(step);
    }
  
    const goToStep = async (step) => initStepper(step);
  
    const initStep = stepNum => {
      const stepsNum = STEPS_NUM;
      WebsiteOptionsTrait.initStep(stepperRef, disabledStepsElements, stepNum, stepsNum, setCurrentStepForData);
    };
  
    /* eslint-disable no-unused-vars */
    const [stepperSteps, setStepperSteps] = useState([
      {
        title: 'Git Repository',
        onClick: () => initStep(1),
      },
      {
        title: 'Add SSH Keys',
        onClick: () => initStep(2),
      },
      {
        title: 'Add Webhook',
        onClick: () => initStep(3),
      },
      {
        title: 'Settings',
        onClick: () => initStep(4),
      }
    ].filter(Boolean))

    // ------------------------------
    // RENDER METHODS
    // ------------------------------

    const renderGitEventLog = () => 
      <EventLogsTable loading={false} commits={gitEventLog}/>

    const renderConnectedFragment = () => gitEventLog ? renderGitEventLog() : <div className='integration-box info-box'>
      <div className="readonly-box">
        <p className="box-label">Git Information:</p>
        <div className="box-row">
          <span className="box-name">Repository URL:</span>
          <span className="box-value">{gitInfo.repository_url}</span>
        </div>
        <div className="box-row">
          <span className="box-name">Repository Branch:</span>
          <span className="box-value">{gitInfo.repository_branch}</span>
        </div>
      </div>
      <div className="writeable-box">
        <p className="box-label">Sync Options:</p>
        {GIT_OPTIONS.map(option => (
          <div key={option.name} className="option-row">
            {JsxHelper.createCheckbox({
              class: 'margin-top-12',
              name: option.name,
              label: option.label,
              tooltip: option.tooltip,
              checked: gitInfo[option.name],
              onChange: () => {
                const newValue = !gitInfo[option.name];
                setOptionsChanged(true);
                setGitInfo({ ...gitInfo, [option.name]: newValue });
                setGitOptions({ ...gitOptions, [option.name]: newValue });
              }
            })}
          </div>
        ))}
      </div>
      <div className='action-buttons display-flex-nowrap' style={{display: 'flex'}}>
        {JsxHelper.createButton({
          label: `Disconnect Git`,
          onClick: () => {
            DialogHelper
              .confirmAction(confirm, 'disconnect', WebsiteHelper.getLabel(website), 'Website', `This will disconnect your website from the Git repository and remove all related settings. Any files that were synced with the repository will remain on your website.`)
              .then(disconnectGit)
          },
          loading: loading === 'disconnect',
        })}
        {JsxHelper.createButton({
          label: `View Logs`,
          classes: 'alt--btn',
          onClick: listGitEventLogs,
          loading: loading === 'event-log',
        })}
        {JsxHelper.createButton({
          label: `Force Git Pull`,
          classes: 'primary--btn',
          onClick: forceGitPull,
          loading: loading === 'force-pull',
        })}
        {JsxHelper.createButton({
          label: `Save Options`,
          classes: 'success--btn',
          disabled: !optionsChanged,
          tooltip: 'You need to change at least one option to save.',
          onClick: saveGitOptions,
          loading: loading === 'save-options',
        })}
      </div>
    </div>

    return (
      <Container className='margin-0'>
        <TitleBar className='titlebar'>
          <TitleBar.Title>Git Integration</TitleBar.Title>
          {gitEventLog && <TitleBar.Actions>
            {JsxHelper.createCloseButton({
              onClick: () => setGitEventLog(false)
            })}
          </TitleBar.Actions>}
        </TitleBar>
        <p className='color-primary subheader'>{subheader}</p>
        {!isGitConnected() ? <Content id='connect-git-wizard'>
  
          <div ref={stepperRef} className='stepper' style={{width: `70%`}}>
            {JsxHelper.createStepper(stepperSteps, currentStep, disabledStepsData)}
          </div>
  
          {currentStepForData === 1 &&
            (GitHelper.renderSetGitRepositoryStep(
              RESOURCE_TYPE,
              gitService,
              setGitService,
              repositoryUrl,
              setRepositoryUrl,
              repositoryError,
              setRepositoryError,
              sshPublicKey,
              repositoryBranch,
              setRepositoryBranch,
              loading || sshLoading,
              () => goToStep(2),
              `e.g. ${website.is_staging ? 'staging' : 'main'}`,
            )
          )}
  
          {currentStepForData === 2 &&
            (GitHelper.renderSetSshKeysStep(
              RESOURCE_TYPE,
              gitService,
              dispatch,
              repositoryUrl,
              sshPublicKey,
              onGenerateKeys,
              onConnectRepository,
              sshLoading,
              loading
            )
          )}
  
          {currentStepForData === 3 && (
            (GitHelper.renderAddWebhookStep(
              RESOURCE_TYPE,
              gitService,
              dispatch,
              repositoryUrl,
              sshPublicKey,
              webhookApiUrl,
              confirmWebhookAdd,
              setConfirmWebhookAdd,
              loading,
              null, // No button
              () => goToStep(4),
            ))
          )}
  
          {currentStepForData === 4 && (
            <WPSForm style={WPSFORM_STYLE}>
              <WPSForm.Fieldset className='display-block' style={{ marginLeft: '2px' }}>
                <legend>Options</legend>
                {GIT_OPTIONS.map(option => (
                  <WPSForm.Row key={option.name}>
                    {JsxHelper.createCheckbox({
                      class: 'margin-top-12',
                      name: option.name,
                      label: option.label,
                      checked: gitOptions[option.name],
                      tooltip: option.tooltip,
                      onChange: handleOnChange
                    })}
                  </WPSForm.Row>
                ))}
              </WPSForm.Fieldset>
              <div className='margin-top-24'>
                {JsxHelper.createButton({
                  label: 'Connect Git',
                  onClick: handleSubmit(connectGit),
                  disabled: !repositoryUrl || !repositoryBranch || repositoryError,
                  loading,
                })}
              </div>
            </WPSForm>
          )}
        </Content> : <Content>
          {loading === 'git-info' ? JsxHelper.createLoadingSpinner() : renderConnectedFragment()}
        </Content>}
      </Container>
    );
};

export default ConnectGit;
